import type { LoaderFunctionArgs } from '~/types/route-args';
import { shouldRevalidateLeaf } from '~/modules/root/util/should-revalidate-leaf';
// eslint-disable-next-line folders/match-regex

import {
    createMetaTitle,
    createMetaDescription,
    getCanonicalHreflang,
    createOGTags,
    mergeMeta,
} from '@modules/seo';
import { getPreloadImageLink } from '~/modules/images';
import { createBlogSchema } from '~/modules/seo/schema/blog-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';

import FeaturedSection from '@modules/articles/component/featured-section/FeaturedSection';
import LatestArticles from '@modules/articles/component/latest-articles/LatestArticles';
import FilmsSection from '@modules/articles/component/films-section/FilmsSection';
import { useLoaderData } from 'react-router';
import FeaturedCarousel from '@modules/articles/component/featured-carousel/FeaturedCarousel';
import { fillExtraInfoForArticleDirectory } from '@modules/articles/util/fill-company-admin.server';
import { adRequest } from '@modules/adverts/api';
import type { loader as archizeenRootLoader } from '~/routes/_app.articles';
import {
    FILMS_CATEGORY_LINK,
    LATEST_ARTICLES_PAGE_SIZE,
    LATEST_FILMS_PAGE_SIZE,
    ROOT_CATEGORY_LINK,
} from '@modules/articles/config/constants';
import type { FeaturedCategorySection } from '@modules/articles/type';
import DirectoryHeader from '~/modules/articles/component/directory-header/DirectoryHeader';
import { shuffleArray } from '~/utils/arrayHelper';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import PageSections from '@modules/articles/component/page-layout/PageSections';
import type { APHandle } from '@modules/root';
import { mapAdDataToFeatureSlide } from '@modules/articles/util/map-ad-data-to-feature-slide';
import { getRouteData } from '~/modules/root/util/get-route-data';
import {
    TOP_ARTICLES_COUNT,
    getArticleDirectory,
} from '~/modules/articles/api/get-article-directory.server';
import { getLatestArticles } from '~/modules/articles/api/get-latest-articles.server';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { getArticleNavigationItems } from '~/modules/articles/api/get-archizeen-common.server';
import React from 'react';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import { ArticlesDirectoryRevamp } from '@modules/articles-revamp/component/ArticlesDirectoryRevamp';
import { isArticlesRevampEnabled } from '@modules/articles-revamp/util/is-articles-revamp-enabled.server';
import { getEditorialHeroPlacement } from '@modules/articles-revamp/api/get-editorial-hero-placement.server';
import { getPremiumArticleFeature } from '@modules/articles-revamp/api/get-premium-article-feature.server';
import { getPremiumFeaturePlacement } from '@modules/articles-revamp/api/get-premium-feature-placement.server';
import { getArticleCategoryItems } from '~/modules/articles/api/get-article-category-items.server';
import { EDITORIAL_PAGE_SIZE } from '@modules/articles-revamp/config/paging';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches } = props;
        const title = createMetaTitle(props, [data?.localisation?.Title]);

        const description = createMetaDescription([
            data?.localisation?.Description,
            `Learn about ${data?.localisation?.Title} through our blog of over 200 articles from expert writers and industry professionals.`,
        ]);

        const blogSchema = createBlogSchema(
            props as unknown as Parameters<typeof createBlogSchema>[0],
        );
        const ldSchema = createLDSchema([blogSchema ?? []]);

        const { rootData } = getRouteData(matches);
        const isMobile = rootData?.device.type === 'mobile';
        const bannerImage =
            data?.articleDirectory?.topReadArticles[0]?.image.src;

        const preloadImages = isMobile
            ? getPreloadImageLink(undefined, bannerImage)
            : getPreloadImageLink(bannerImage);

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
            }),
            ...getCanonicalHreflang(props),
            ...ldSchema,
            ...(preloadImages ?? []),
        ];
    },
);

export const handle: APHandle = {
    isCategoryPage: true,
    hideContactArchiPro: false,
    fadeTopNavAfterPosition: 200,
    // The revamped masthead runs full-bleed under the header, so the header
    // goes transparent over it and picks up the logomark, matching the project
    // and professional detail pages. Resolved against loader data so the legacy
    // directory keeps the solid header while the flag is off.
    header: (loaderData) =>
        (loaderData as { showRevamp?: boolean } | undefined)?.showRevamp
            ? { headerVariant: 'transparent', showLogomark: true }
            : undefined,
};

const SHOW_FEATURE_CAROUSEL: string[] = [];

export const loader = async (args: LoaderFunctionArgs) => {
    const { context, request } = args;
    const { user } = context;
    const requestUrl = request.url;

    const [
        articleDirectory,
        latestArticles,
        latestFilms,
        localisation,
        bannerAds,
        navItems,
    ] = await Promise.all([
        getArticleDirectory(context),
        getLatestArticles({
            ...context,
            categoryLink: ROOT_CATEGORY_LINK,
            limit: LATEST_ARTICLES_PAGE_SIZE,
            offset: 0,
        }),
        getLatestArticles({
            ...context,
            categoryLink: FILMS_CATEGORY_LINK,
            limit: LATEST_FILMS_PAGE_SIZE,
            offset: 0,
        }),
        getPageLocalisation(args),
        adRequest({
            type: 'ArchizeenBannerPlacement',
            requestUrl,
            ...args.context,
        }),
        getArticleNavigationItems(context),
    ]);

    // Only fetched behind the gate, so the legacy directory costs nothing extra.
    const showRevamp = isArticlesRevampEnabled(context, request);

    // The editor's choice of masthead article, where one is set. Null on a day
    // nobody has curated, which is the normal case — the component then falls
    // back to the most-read article.
    // The premium feature: the editor's placement where one is set, otherwise
    // the newest article carrying the entitlement. Curation overrides rather
    // than gates, as it does for the masthead — the slot still fills on a day
    // nobody has chosen, and the module claims "Premium article", so the
    // fallback has to be one.
    //
    // Both are fetched rather than the fallback awaiting the placement: a
    // curated placement is the exception, so serialising would pay a round trip
    // on almost every request to save one on a few.
    const [heroPlacement, premiumPlacement, premiumEntitled] = showRevamp
        ? await Promise.all([
              getEditorialHeroPlacement({
                  ...context,
                  targetPath: ROOT_CATEGORY_LINK,
              }),
              getPremiumFeaturePlacement({
                  ...context,
                  targetPath: ROOT_CATEGORY_LINK,
              }),
              getPremiumArticleFeature({
                  ...context,
                  categoryLink: ROOT_CATEGORY_LINK,
              }),
          ])
        : [null, null, null];

    const premiumFeature = premiumPlacement ?? premiumEntitled;

    // The masthead article, resolved here because the carousel below is built
    // around it. Mirrors what the page component renders.
    const heroArticle = heroPlacement ?? articleDirectory?.topReadArticles?.[0];

    // The carousel drops the masthead article: unfiltered it opens on the very
    // article the reader is already looking at, since the masthead's own
    // fallback is the most-read one. The listing query reads a fourth top-read
    // article so removing it still leaves three slides. Banner ads are not
    // articles, so the filter is a no-op on that path.
    const bannerAdsShuffled = bannerAds ? shuffleArray(bannerAds) : [];
    const featuredSlides = (
        bannerAdsShuffled.length
            ? bannerAdsShuffled.map(mapAdDataToFeatureSlide)
            : (articleDirectory?.topReadArticles ?? [])
    )
        .filter((slide) => slide.link !== heroArticle?.link)
        .slice(0, TOP_ARTICLES_COUNT);

    // Every article the page promotes above the grid: the masthead, the featured
    // carousel and the premium feature. The listing knows nothing of any of it,
    // so without this each one arrives again as an ordinary card and the reader
    // meets it twice.
    //
    // Resolved here rather than in the component so one list both sizes the read
    // below and filters the grid — derived twice, the spare would not match what
    // is actually dropped and the first page would run short.
    //
    // Deduped, and by link rather than ID: a curated placement carries the
    // article's URL but not its ID, and the masthead article is normally the
    // carousel's first slide as well.
    const promotedLinks = showRevamp
        ? [
              ...new Set(
                  [
                      heroArticle?.link,
                      premiumFeature?.link,
                      ...featuredSlides.map((slide) => slide.link),
                  ].filter((link): link is string => Boolean(link)),
              ),
          ]
        : [];

    // The revamped grid pages the whole root listing rather than stitching the
    // per-category featured sections together: it needs enough rows to inject
    // the promo band and the premium feature between, and a real total so the
    // grid can page.
    //
    // The first page reads one spare row per promoted article, since those are
    // dropped from it below — without them a four-column grid ends on a short
    // final row. Later pages promote nothing, so they read a plain page.
    //
    // `?page=N` is served rather than ignored, because the grid's Load More is a
    // real anchor at that URL: a crawler that runs no JavaScript walks the
    // listing through it, and every page answering with the first page's rows
    // would be duplicate content at as many URLs as there are pages.
    const requestedPage = Math.max(
        1,
        Number.parseInt(
            new URL(request.url).searchParams.get('page') ?? '1',
            10,
        ) || 1,
    );
    const rootArticles = showRevamp
        ? await getArticleCategoryItems({
              ...context,
              request: new Request(new URL(ROOT_CATEGORY_LINK, request.url), {
                  headers: request.headers,
              }),
              window:
                  requestedPage === 1
                      ? {
                            offset: 0,
                            limit: EDITORIAL_PAGE_SIZE + promotedLinks.length,
                        }
                      : {
                            offset: (requestedPage - 1) * EDITORIAL_PAGE_SIZE,
                            limit: EDITORIAL_PAGE_SIZE,
                        },
          })
        : undefined;

    fillExtraInfoForArticleDirectory(
        user,
        articleDirectory,
        latestArticles.articles,
        latestFilms.articles,
    );

    return {
        deviceType: context.device.type,
        articleDirectory,
        latestArticles,
        latestFilms,
        localisation,
        bannerAds: bannerAdsShuffled,
        navItems,
        featuredSlides,
        showRevamp,
        heroPlacement,
        premiumFeature,
        promotedLinks,
        rootArticles: rootArticles
            ? {
                  articles: rootArticles.articles,
                  totalCount: rootArticles.totalCount,
              }
            : undefined,
    };
};

const ArchizeenDirectoryPage = () => {
    const {
        articleDirectory,
        latestArticles,
        latestFilms,
        navItems,
        featuredSlides,
        showRevamp,
        heroPlacement,
        premiumFeature,
        promotedLinks,
        rootArticles,
    } = useLoaderData<typeof loader>();

    const { common } = useLoaderData<typeof archizeenRootLoader>();
    // Keep the first value returned by loader to avoid changing when loader revalidate
    const featuredSlidesMemoized = useLocationMemo(featuredSlides);

    const { setCategory } = useCurrentCategory();

    // Set the current category for header navigation
    React.useEffect(() => {
        setCategory('Archizeen');
        return () => setCategory(null);
    }, [setCategory]);

    // Gate sits below the hooks so both paths run the same hook order.
    if (showRevamp) {
        // The masthead: an editor's curated placement where one is set,
        // otherwise the most-read article. Curation is an override rather than
        // a requirement, so the page still leads with something on a day nobody
        // has chosen. The automatic fallback comes from the top-read slides
        // rather than the tile list, since only that shape carries the
        // article's intro text and its full-bleed banner image.
        //
        // The grid is handed the listing as served; it drops the articles the
        // masthead and premium module have already promoted, so that one rule
        // sits with the sections that promote them and applies to every page
        // rather than only the first.
        const heroArticle =
            heroPlacement ?? articleDirectory?.topReadArticles?.[0];

        return (
            <ArticlesDirectoryRevamp
                hero={heroArticle}
                featured={featuredSlidesMemoized.map((slide) => ({
                    id: slide.ID,
                    title: slide.title,
                    link: slide.link,
                    imageSrc: slide.image?.src,
                    hasVideo: slide.hasVideo,
                }))}
                premium={premiumFeature ?? undefined}
                excludeLinks={promotedLinks}
                articles={rootArticles?.articles ?? []}
                totalCount={
                    rootArticles?.totalCount ??
                    rootArticles?.articles.length ??
                    0
                }
                navItems={navItems ?? []}
            />
        );
    }

    if (!articleDirectory) return null;

    const { featuredSections } = articleDirectory;
    const { articles: films, totalCount } = latestFilms;

    const loadMoreFilms =
        films.length < totalCount ? FILMS_CATEGORY_LINK : undefined;

    return (
        <>
            <DirectoryHeader
                navItems={navItems ?? []}
                content={common?.contentTop}
            />
            <PageSections>
                <FeaturedCarousel
                    featuredSlides={featuredSlidesMemoized}
                    imageLazyLoading={'eager'}
                />
                <LatestArticles
                    title={'The Latest'}
                    latestArticles={latestArticles.articles}
                />
                {featuredSections.map((section) => (
                    <FeaturedSection
                        key={section.link}
                        featuredSection={section as FeaturedCategorySection}
                        showFeaturedSlides={SHOW_FEATURE_CAROUSEL.includes(
                            section.title.toLowerCase(),
                        )}
                    />
                ))}
                {latestFilms.articles.length > 0 && (
                    <FilmsSection
                        latestArticles={latestFilms.articles}
                        loadMoreLink={loadMoreFilms}
                    />
                )}
            </PageSections>
        </>
    );
};

export const shouldRevalidate = shouldRevalidateLeaf;
export default ArchizeenDirectoryPage;
